/**
 * RouteProgressBar — the 2px indeterminate bar under the header that says
 * "this page is still loading".
 *
 * Mounted once in `AppShell` (like `ImpersonationBanner` / `ProcessingBanner`)
 * and driven by the `usePageLoading` counter, so it covers every page that
 * reports in — including the ones whose own skeletons sit far below the fold.
 *
 * Deliberately indeterminate: none of the underlying fetches report progress,
 * so a percentage would be invented. It's also `aria-hidden` — the per-page
 * skeletons already announce the wait via `role="status"`, and a second
 * announcement on every navigation would be noise for screen-reader users.
 *
 * The 120 ms appearance delay keeps a cached, instant page from flashing a
 * bar; anything slower than that is a wait worth showing.
 */

import { useEffect, useState } from 'react';
import { getInFlightCount, onPageLoadingChanged } from '../../hooks/usePageLoading';

const APPEAR_AFTER_MS = 120;

export function RouteProgressBar() {
  const [busy, setBusy] = useState(() => getInFlightCount() > 0);

  useEffect(() => onPageLoadingChanged(() => setBusy(getInFlightCount() > 0)), []);

  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!busy) {
      setVisible(false);
      return;
    }
    const t = window.setTimeout(() => setVisible(true), APPEAR_AFTER_MS);
    return () => window.clearTimeout(t);
  }, [busy]);

  if (!visible) return null;

  return (
    <div
      aria-hidden="true"
      data-testid="route-progress-bar"
      style={{
        position: 'relative',
        height: 2,
        overflow: 'hidden',
        background: 'var(--bg-tertiary)',
        flex: 'none',
      }}
    >
      <div
        className="compass-route-progress"
        style={{ position: 'absolute', inset: 0, background: 'var(--accent)' }}
      />
    </div>
  );
}
